<template>
  <!-- 文本域（textarea）+ 图片上传：
       - 文本：v-model:text
       - 图片：v-model:images（逗号分隔的完整 url 串，与 ImageUpload 一致）
       - 剪贴板粘贴图片由本组件内部完成上传（uploadApi + folder） -->
  <div class="yq-textarea">
    <div class="textarea">
      <el-input
          :model-value="text"
          type="textarea"
          :rows="rows"
          resize="vertical"
          :placeholder="placeholder"
          @update:model-value="val => $emit('update:text', val)"
          @paste="handlePaste"/>
      <transition name="fade">
        <div v-if="showMask" class="yq-textarea-mask">
          <span class="yq-textarea-spinner"/>
          {{ uploadingText }}
        </div>
      </transition>
    </div>
    <div class="images">
      <!-- 手动选择图片：上传/预览/删除均由 ImageUpload 负责，值经 v-model:images 与父级同步 -->
      <image-upload
          :model-value="images"
          :url-only="true"
          :limit="limit"
          :java-url="uploadApi"
          :folder="folder"
          accept="image/*"
          class="yq-textarea-upload"
          @update:model-value="val => $emit('update:images', val)"/>
      <!-- 右侧操作区（如"发布"按钮） -->
      <slot></slot>
    </div>
  </div>
</template>

<script>
import axios from "@/util/axios";
import imageUtil from "@/util/imageUtil";
import ImageUpload from "@/components/h5/image/ImageUpload.vue";

export default {
  name: "YqTextarea",
  components: {ImageUpload},
  props: {
    /** 文本内容，使用 v-model:text 绑定 */
    text: {type: String, default: ""},
    /** 图片 url 串（逗号分隔完整 url），使用 v-model:images 绑定 */
    images: {type: String, default: ""},
    /** 上传图片的 API 接口地址 */
    uploadApi: {type: String, default: "/basedata/common/file/uploadImage"},
    /** 图片上传后存储的文件夹 */
    folder: {type: String, default: "common"},
    /** 上传图片的最大数量 */
    limit: {type: Number, default: 9},
    /** 占位提示 */
    placeholder: {type: String, default: "请输入内容（可 Ctrl+V 粘贴图片）"},
    /** textarea 行数 */
    rows: {type: Number, default: 3},
    /** 外部强制显示"上传中"遮罩（一般无需传，内部粘贴上传会自动显示） */
    uploading: {type: Boolean, default: false},
    /** 上传中遮罩文案 */
    uploadingText: {type: String, default: "正在上传图片…"},
    /** 剪贴板粘贴图片的大小上限（MB） */
    maxSizeMb: {type: Number, default: 10},
  },
  emits: ["update:text", "update:images"],
  data() {
    return {
      // 内部粘贴上传计数（>0 显示遮罩）
      pasteUploading: 0,
    };
  },
  computed: {
    showMask() {
      return this.uploading || this.pasteUploading > 0;
    },
  },
  methods: {
    /** 当前已选图片列表 */
    imageList() {
      return (this.images || "").split(",").filter(Boolean);
    },
    /** 剪贴板粘贴：仅接受图片、限制大小，通过后由本组件内部上传 */
    handlePaste(ev) {
      const items = (ev.clipboardData && ev.clipboardData.items) || [];
      let handledImage = false;
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        // 纯文本（kind=string）交由默认粘贴行为
        if (item.kind !== "file") {
          continue;
        }
        const file = item.getAsFile();
        if (!file) {
          continue;
        }
        if (!/^image\//i.test(file.type)) {
          this.$message.error("剪贴板仅支持粘贴图片");
          ev.preventDefault();
          return;
        }
        if (file.size > this.maxSizeMb * 1024 * 1024) {
          this.$message.error("图片不能超过 " + this.maxSizeMb + "MB");
          ev.preventDefault();
          return;
        }
        handledImage = true;
        this.uploadImageFile(file);
      }
      if (handledImage) {
        ev.preventDefault(); // 阻止把图片二进制/路径塞进文本框
      }
    },
    /** 上传单个图片文件，成功后把完整 url 追加到 images 并回写父级 */
    async uploadImageFile(file) {
      if (!file) {
        return;
      }
      if (this.imageList().length >= this.limit) {
        this.$message.warning("最多上传 " + this.limit + " 个图片");
        return;
      }
      const formData = new FormData();
      formData.append("file", file);
      formData.append("folder", this.folder);
      this.pasteUploading++;
      try {
        const res = await axios.post({url: this.uploadApi, data: formData});
        const url = imageUtil.getFileUrlSingle(this.toRelUrl(res));
        if (!url) {
          this.$message.error("上传失败，请重试");
          return;
        }
        const after = this.imageList();
        if (after.length >= this.limit) {
          this.$message.warning("最多上传 " + this.limit + " 个图片");
          return;
        }
        after.push(url);
        this.$emit("update:images", after.join(","));
      } catch (e) {
        this.$message.error("上传失败");
      } finally {
        this.pasteUploading = Math.max(0, this.pasteUploading - 1);
      }
    },
    /** 上传接口返回归一化为相对路径（可能是字符串 / {data} / {url}） */
    toRelUrl(res) {
      if (!res) {
        return "";
      }
      if (typeof res === "string") {
        return this.stripDomain(res);
      }
      if (typeof res === "object") {
        if (typeof res.data === "string") {
          return this.stripDomain(res.data);
        }
        if (typeof res.url === "string") {
          return this.stripDomain(res.url);
        }
      }
      return "";
    },
    stripDomain(url) {
      if (!url) {
        return url;
      }
      const base = process.env.VUE_APP_FILE_URL || "";
      return base && url.indexOf(base) === 0 ? url.substring(base.length) : url;
    },
  },
};
</script>

<style lang="scss" scoped>
.yq-textarea {
  .images {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-top: 8px;
    .yq-textarea-upload {
      flex: 1;
      min-width: 0;
      ::v-deep(.el-upload--picture-card) {
        width: 64px;
        height: 64px;
        line-height: 64px;
      }
      ::v-deep(.el-upload-list__item) {
        width: 64px;
        height: 64px;
      }
    }
  }
}
.textarea {
  position: relative;
  .yq-textarea-mask {
    position: absolute;
    inset: 0;
    z-index: 3;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    background: rgba(255, 255, 255, 0.7);
    color: #409eff;
    font-size: 13px;
    border-radius: 4px;
    cursor: wait;
    .yq-textarea-spinner {
      width: 16px;
      height: 16px;
      border: 2px solid rgba(64, 158, 255, 0.25);
      border-top-color: #409eff;
      border-radius: 50%;
      animation: yq-textarea-spin 0.8s linear infinite;
    }
  }
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
@keyframes yq-textarea-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
